'use client';

import { useRef, useState, ComponentProps } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import ReviewFormModal from '@alltrails/reviews/components/NewReviewFormModal/NewReviewFormModal';
import ReviewsBase from '@alltrails/reviews/components/ReviewsBase/ReviewsBase';
import type { Review } from '@alltrails/types/Review';
import logWebTrailLinkClicked from '@alltrails/amplitude/events/logWebTrailLinkClicked';
import logPhotoCarouselSwiped from '@alltrails/amplitude/events/logPhotoCarouselSwiped';
import SectionHeader from './SectionHeader';
import styles from './Reviews.module.scss';
import { getAnalyticsPageType } from '@/utils/getAnalyticsPageType';
import { useSelector } from '@/utils/redux';
import { LocationDetailsResponseType } from '@/types/locationDetailsPages/shared';

const pageSize = 6;
const maxReviewsToReveal = 20;

type ReviewsListProps = {
  type: LocationDetailsResponseType;
  locationName: string;
  openReviewFormModal: (trailFormProps: ComponentProps<typeof ReviewFormModal>) => void;
  reviews: Review[];
  mentionReviews?: Review[];
  isBot?: boolean;
};

const ReviewsList = ({ type, locationName, openReviewFormModal, reviews, mentionReviews = [], isBot }: ReviewsListProps) => {
  const context = useSelector(state => state.context);
  const reviewsRef = useRef<HTMLDivElement>(null);
  const carouselSwipedRef = useRef(new Set<Review['id']>());
  const pageType = getAnalyticsPageType(type);

  const [showingReviewsCount, setShowingReviewsCount] = useState(isBot ? maxReviewsToReveal : pageSize);

  let trailReviews = reviews;

  // Currently, the mentioned reviews section/experience is only for POI pages
  // https://www.figma.com/design/Xmdcv0j315iIELXRMl8UzJ/POIs-Web-%5BHandoff%5D?node-id=330-267828
  let mentionReviewsCount = (type === 'poi' && mentionReviews?.length) || 0;
  let maxReviewsToShow = pageSize;
  if (mentionReviewsCount > 0) {
    const mentionReviewIds = mentionReviews.map(mentionReview => mentionReview.id);
    // Exclude mention reviews from the trail review set
    trailReviews = reviews.filter(review => !mentionReviewIds.includes(review.id));
    maxReviewsToShow = Math.min(maxReviewsToReveal, mentionReviewsCount + trailReviews.length);
  }

  const onReviewPhotosScroll = (review: Review) => {
    if (!carouselSwipedRef.current.has(review.id)) {
      carouselSwipedRef.current.add(review.id);
      logPhotoCarouselSwiped({
        carousel_item_id: review.id.toString(),
        page_type: pageType,
        photo_carousel_type: 'reviews'
      });
    }
  };

  const onLoadMoreReviews = () => {
    setShowingReviewsCount(showingReviewsCount + pageSize);
  };

  const reviewsBaseProps: Omit<ComponentProps<typeof ReviewsBase>, 'reviews' | 'canLoadMore'> = {
    context,
    localizeOnLoad: true,
    onActivityLinkClick: () => {},
    onLoadMore: onLoadMoreReviews,
    openReviewFormModal,
    preferHTML: true,
    reportLocation: 'location web page',
    total: reviews.length,
    onReviewPhotosScroll,
    bottomLinkType: 'trail' as 'trail',
    onTrailLinkClick: () => {
      if (pageType) logWebTrailLinkClicked({ page_type: pageType, section_type: 'Trail Reviews' });
    }
  };

  return (
    <div className={classNames(styles.ugcContainer, { [styles.hasSections]: mentionReviewsCount > 0 })}>
      {mentionReviewsCount > 0 && (
        <>
          <SectionHeader
            className={styles.firstReviewSection}
            header={<FormattedMessage defaultMessage="Reviews that mention {locationName}" values={{ locationName }} />}
          />
          <ReviewsBase
            ref={reviewsRef}
            {...reviewsBaseProps}
            reviews={mentionReviews.slice(0, showingReviewsCount)}
            canLoadMore={showingReviewsCount < mentionReviewsCount}
            className={styles.reviewSection}
          />
        </>
      )}
      {trailReviews.length > 0 && showingReviewsCount > mentionReviewsCount && (
        <>
          {mentionReviewsCount > 0 && (
            <SectionHeader header={<FormattedMessage defaultMessage="Reviews of nearby trails" />} className={styles.secondReviewSection} />
          )}
          <ReviewsBase
            ref={reviewsRef}
            {...reviewsBaseProps}
            reviews={trailReviews.slice(0, showingReviewsCount - mentionReviewsCount)}
            canLoadMore={showingReviewsCount < maxReviewsToShow}
            className={styles.reviewSection}
          />
        </>
      )}
    </div>
  );
};

export default ReviewsList;
